// Single source of truth for turning a stored photo/signature reference into a
// URL the browser can load.
//
// Why this exists: four pages each hand-rolled their own version, and most did
// `/uploads/job-photos/${path.split('/').pop()}` — discarding the folder. Any
// photo stored outside job-photos (commercial-sld-photos, maintenance,
// signatures) therefore resolved to a URL that does not exist. Worse, the SPA
// catch-all answers those with 200 + index.html instead of 404, so the failure
// was silent: <img> just showed nothing and jsPDF drew garbage.
//
// Stored references come in three shapes:
//   "/uploads/commercial-sld-photos/x.jpg"                  — already servable
//   "/var/www/.../sowash-backend/uploads/job-photos/x.jpg"  — legacy absolute
//                                                             filesystem path
//   "x.jpg"                                                 — bare filename
//
// The backend now writes the first shape (see publicUploadPath in
// ciFOSchedulesRoutes.js); the second is legacy data that predates that fix.

// Media is served from the app host WITHOUT the /api suffix — REACT_APP_API_BASE_URL
// already includes /api, so strip it before building an asset URL.
const API = process.env.REACT_APP_API_BASE_URL || '';
export const MEDIA_BASE = API
  ? API.replace(/\/api\/?$/, '')
  : 'https://app.sowashusa.com';

/**
 * Build a loadable URL for a stored media reference.
 *
 * @param {string|{url?:string,path?:string,name?:string}} raw stored reference
 * @param {string} fallbackFolder folder to assume ONLY for a bare filename
 * @returns {string} absolute URL, or "" when there is nothing to show
 */
export const mediaUrl = (raw, fallbackFolder = 'job-photos') => {
  if (!raw) return '';

  const source =
    typeof raw === 'object' ? raw.url || raw.path || raw.name || '' : raw;

  // Strip JSON punctuation left behind when a photo array is split naively.
  const p = String(source).replace(/["[\]\\]/g, '').trim();
  if (!p) return '';

  if (p.startsWith('http')) return p;

  // Keep the real folder for both servable paths and legacy filesystem paths.
  const uploadsAt = p.indexOf('/uploads/');
  if (uploadsAt >= 0) return `${MEDIA_BASE}${p.slice(uploadsAt)}`;

  return `${MEDIA_BASE}/uploads/${fallbackFolder}/${p.split('/').pop()}`;
};

/**
 * Parse a stored photo column into an array of references.
 * Columns hold a JSON array string, occasionally double-encoded, and sometimes
 * a plain comma-separated list.
 */
export const parsePhotoList = (raw) => {
  if (!raw) return [];
  let v = raw;
  for (let i = 0; i < 2 && typeof v === 'string'; i += 1) {
    try { v = JSON.parse(v); } catch { break; }
  }
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string') return v.split(',').map(s => s.trim()).filter(Boolean);
  return v ? [v] : [];
};

export default mediaUrl;
